// src/components/ThemeToggleButton.js - VERSION AMÉLIORÉE

import React, { useState, useCallback, memo, useEffect } from 'react';
import { useTheme } from '../ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faAdjust } from '@fortawesome/free-solid-svg-icons';
import './ThemeToggleButton.css';

const ThemeToggleButton = memo(() => {
  const { theme, toggleTheme, systemPreference } = useTheme();
  const [isToggling, setIsToggling] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Animation de toggle
  const handleToggle = useCallback(async () => {
    if (isToggling) return;
    
    setIsToggling(true);
    
    try {
      await toggleTheme();
    } catch (error) {
      console.error('Erreur lors du changement de thème:', error);
    } finally {
      setTimeout(() => setIsToggling(false), 300);
    }
  }, [toggleTheme, isToggling]);
  
  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + T pour basculer le thème
      if ((e.ctrlKey || e.metaKey) && e.key === 't') {
        e.preventDefault();
        handleToggle();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleToggle]);
  
  // Déterminer l'icône et le texte
  const getThemeInfo = () => {
    switch (theme) {
      case 'dark':
        return {
          icon: faSun,
          label: 'Mode clair',
          next: 'clair',
          ariaLabel: 'Basculer vers le mode clair',
          tooltip: 'Mode sombre actif (cliquer pour mode clair)'
        };
      case 'light':
        return {
          icon: faMoon,
          label: 'Mode sombre',
          next: 'sombre',
          ariaLabel: 'Basculer vers le mode sombre',
          tooltip: 'Mode clair actif (cliquer pour mode sombre)'
        };
      default: // 'system' ou autre
        return {
          icon: faAdjust,
          label: 'Système',
          next: systemPreference === 'dark' ? 'clair' : 'sombre',
          ariaLabel: 'Basculer vers le thème du système',
          tooltip: `Thème système (${systemPreference})`
        };
    }
  };
  
  const themeInfo = getThemeInfo();
  
  return (
    <div className="theme-toggle-container">
      <button
        className={`theme-toggle-btn ${isToggling ? 'toggling' : ''} theme-${theme}`}
        onClick={handleToggle}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label={themeInfo.ariaLabel}
        title={themeInfo.tooltip}
        disabled={isToggling}
        data-theme={theme}
        data-next-theme={themeInfo.next}
      >
        <div className="theme-toggle-inner">
          {/* Icône principale */}
          <FontAwesomeIcon 
            icon={themeInfo.icon} 
            className={`theme-icon ${isToggling ? 'spinning' : ''}`}
            aria-hidden="true"
          />
          
          {/* Animation pendant le toggle */}
          {isToggling && (
            <div className="theme-toggle-loading">
              <div className="loading-spinner"></div>
            </div>
          )}
          
          {/* Badge pour indiquer le thème système */}
          {theme === 'system' && (
            <span className="theme-system-badge" aria-hidden="true">
              <FontAwesomeIcon icon={faAdjust} />
            </span>
          )}
        </div>
        
        {/* Texte optionnel (peut être caché sur mobile) */}
        <span className="theme-label d-none d-sm-inline">
          {themeInfo.label}
        </span>
      </button>
      
      {/* Tooltip personnalisé */}
      {showTooltip && (
        <div className="theme-tooltip" role="tooltip">
          {themeInfo.tooltip}
          <div className="tooltip-arrow"></div>
          <div className="tooltip-shortcut">
            <kbd>Ctrl</kbd> + <kbd>T</kbd> pour basculer
          </div>
        </div>
      )}
      
      {/* Indicateur visuel pour les préférences système */}
      {theme === 'system' && (
        <div className="system-theme-indicator" aria-hidden="true">
          <small className="text-muted">
            Système: {systemPreference === 'dark' ? '🌙' : '☀️'}
          </small>
        </div>
      )}
    </div>
  );
});

ThemeToggleButton.displayName = 'ThemeToggleButton';

export default ThemeToggleButton;